import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, User, Loader2, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const AUTH_IMG = "/products/corner-karo-velvet.jpg";

export default function Auth({ mode: initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(mode === "login" ? `Bienvenue, ${data.name} !` : "Compte créé avec succès !");
      navigate(location.state?.from || "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto grid min-h-[calc(100vh-140px)] max-w-6xl items-center gap-12 px-6 py-12 lg:grid-cols-2 lg:px-8">
      <div className="relative hidden overflow-hidden rounded-[2rem] shadow-luxe lg:block">
        <img src={AUTH_IMG} alt="" className="h-[560px] w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 to-transparent" />
        <div className="absolute bottom-0 p-10">
          <span className="font-serif text-3xl text-white">shop<span className="text-gold">·</span>canapé</span>
          <p className="mt-2 max-w-xs text-white/70">Rejoignez notre maison et accédez à vos commandes, favoris et offres privées.</p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-md">
        <p className="mb-2 font-accent text-xs uppercase tracking-[0.25em] text-gold-dark">{mode === "login" ? "Bon retour" : "Bienvenue"}</p>
        <h1 className="font-serif text-4xl font-medium">{mode === "login" ? "Connexion" : "Créer un compte"}</h1>

        <button onClick={googleLogin} data-testid="google-login-button"
          className="mt-8 flex w-full items-center justify-center gap-3 rounded-full border border-border bg-card py-3.5 text-sm font-semibold transition-colors hover:bg-secondary">
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
          Continuer avec Google
        </button>

        <div className="my-6 flex items-center gap-4 text-xs text-muted-foreground">
          <div className="h-px flex-1 bg-border" /> OU <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
          <AnimatePresence mode="wait">
            {mode === "register" && (
              <motion.div key="name" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                <IconField icon={User} placeholder="Nom complet" value={form.name} onChange={set("name")} required testid="auth-name" />
              </motion.div>
            )}
          </AnimatePresence>
          <IconField icon={Mail} type="email" placeholder="Adresse email" value={form.email} onChange={set("email")} required testid="auth-email" />
          <IconField icon={Lock} type="password" placeholder="Mot de passe" value={form.password} onChange={set("password")} required testid="auth-password" />

          {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

          <button type="submit" disabled={loading} data-testid="auth-submit"
            className="group flex w-full items-center justify-center gap-2 rounded-full bg-obsidian py-4 text-sm font-semibold text-cream transition-colors hover:bg-gold hover:text-obsidian disabled:opacity-60">
            {loading ? <Loader2 size={17} className="animate-spin" /> : <>{mode === "login" ? "Se connecter" : "Créer mon compte"} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {mode === "login" ? "Pas encore de compte ? " : "Déjà inscrit·e ? "}
          <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="font-semibold text-gold-dark hover:underline" data-testid="auth-toggle">
            {mode === "login" ? "Créer un compte" : "Se connecter"}
          </button>
        </p>
        <Link to="/" className="mt-4 block text-center text-xs text-muted-foreground hover:text-foreground">← Retour à l'accueil</Link>
      </div>
    </div>
  );
}

function IconField({ icon: Icon, testid, ...props }) {
  return (
    <div className="flex items-center gap-3 rounded-full border border-border bg-card px-5 py-3.5 transition-colors focus-within:border-gold">
      <Icon size={17} className="text-muted-foreground" />
      <input {...props} data-testid={testid} className="flex-1 bg-transparent text-sm outline-none" />
    </div>
  );
}
